import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { StorageService } from '../services/StorageService';
import { lessonData } from '../data/lessonData';

const { FiSave, FiCheckCircle, FiCircle, FiHeart, FiFeather, FiTrendingUp } = FiIcons;

const LessonPage = ({ lessonProgress, updateLessonProgress }) => {
  const { lessonNumber } = useParams();
  const lesson = lessonData[lessonNumber];
  const [formData, setFormData] = useState({});
  const [saveStatus, setSaveStatus] = useState('');

  useEffect(() => {
    if (lesson) {
      const savedData = StorageService.getLessonData(lessonNumber);
      setFormData(savedData);
    }
  }, [lessonNumber, lesson]);

  const handleInputChange = (fieldKey, value) => {
    const newData = { ...formData, [fieldKey]: value };
    setFormData(newData);
    clearTimeout(window.saveTimeout);
    window.saveTimeout = setTimeout(() => {
      StorageService.saveLessonData(lessonNumber, newData);
      setSaveStatus('Saved automatically');
      setTimeout(() => setSaveStatus(''), 2000);
    }, 1000);
  };

  const handleManualSave = () => {
    StorageService.saveLessonData(lessonNumber, formData);
    setSaveStatus('Saved successfully!');
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const toggleCompletion = () => {
    updateLessonProgress(lessonNumber, !lessonProgress[lessonNumber]);
  };

  const getInterpretation = (score) => {
    if (!lesson.interpretation) return null;
    
    const numScore = parseInt(score);
    if (isNaN(numScore)) return null;
    
    return lesson.interpretation.ranges.find(range => 
      numScore >= range.min && numScore <= range.max
    );
  };

  if (!lesson) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16">
        <h1 className="font-serif text-2xl text-gray-600">Lesson not found</h1>
      </div>
    );
  }

  // Always calculate interpretation based on current form data
  const currentInterpretation = getInterpretation(formData.lesson1_score);

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="bg-white rounded-2xl shadow-soft p-8 paper-texture"
      >
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-blue-600 rounded-lg flex items-center justify-center shadow-soft">
                <FiFeather className="text-white text-xl" />
              </div>
              <div>
                <h1 className="font-serif text-3xl md:text-4xl text-primary font-bold leading-tight">
                  {lesson.title}
                </h1>
                <p className="font-sans text-sm text-gray-500 mt-1 uppercase tracking-wide">Lesson {lessonNumber}</p>
              </div>
            </div>
            <p className="font-sans text-lg text-gray-600 leading-relaxed max-w-3xl">
              {lesson.lesson_focus}
            </p>
          </div>
          <button 
            onClick={toggleCompletion}
            className="ml-6 text-2xl hover:scale-110 transition-transform"
            title={lessonProgress[lessonNumber] ? 'Mark as incomplete' : 'Mark as complete'}
          >
            <SafeIcon 
              icon={lessonProgress[lessonNumber] ? FiCheckCircle : FiCircle} 
              className={lessonProgress[lessonNumber] ? 'text-green-500' : 'text-gray-300'} 
            />
          </button>
        </div>
        {saveStatus && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="inline-flex items-center gap-2 text-sm text-green-700 bg-green-50 px-4 py-2 rounded-lg"
          >
            <FiCheckCircle className="text-green-600" />
            {saveStatus}
          </motion.div>
        )}
      </motion.div>

      {/* Lesson Content */}
      <div className="space-y-6">
        {lesson.sections ? (
          lesson.sections.map((section, index) => (
            <motion.div 
              key={index} 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ delay: index * 0.1 }} 
              className="bg-white rounded-2xl shadow-soft p-8 paper-texture"
            >
              <h2 className="font-serif text-2xl text-primary mb-6 flex items-center gap-3">
                <SafeIcon icon={FiHeart} className="text-accent" />
                {section.header}
              </h2>
              
              {section.content && (
                <p className="font-sans text-gray-700 mb-6 leading-relaxed text-lg">
                  {section.content}
                </p>
              )}

              {/* Interpretation Section for Lesson 1 */}
              {section.interpretation && (
                <div className="mb-6">
                  {currentInterpretation ? (
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100"
                    >
                      <h3 className="font-serif text-xl text-primary font-semibold mb-4 flex items-center gap-2">
                        <SafeIcon icon={FiTrendingUp} className="text-accent" />
                        INTERPRETATION:
                      </h3>
                      <div className="p-4 rounded-lg border-2 border-accent bg-white shadow-soft">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-sans font-bold text-accent text-lg">
                            {currentInterpretation.min}–{currentInterpretation.max}:
                          </span>
                          <motion.span
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-accent text-white text-xs px-3 py-1 rounded-full font-semibold"
                          >
                            YOUR SCORE
                          </motion.span>
                        </div>
                        <p className="font-sans text-gray-800 font-medium">
                          You're in the <span className="font-semibold text-primary">{currentInterpretation.stage}</span> → {currentInterpretation.focus}
                        </p>
                      </div>
                    </motion.div>
                  ) : (
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <p className="font-sans text-gray-500 text-center">
                        Enter a score above to see your interpretation
                      </p>
                    </div>
                  )}
                </div>
              )}

              {section.fields && section.fields.map((field, fieldIndex) => (
                <div key={fieldIndex} className="mb-6">
                  <label className="block font-sans font-semibold text-gray-700 mb-3 text-lg">
                    {field.label}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea
                      value={formData[field.store_as] || ''}
                      onChange={(e) => handleInputChange(field.store_as, e.target.value)}
                      className="literary-input w-full p-4 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 font-sans"
                      rows={4}
                      placeholder="Share your thoughts..."
                    />
                  ) : field.type === 'number' ? (
                    <input
                      type="number"
                      min="0"
                      max="50"
                      value={formData[field.store_as] || ''}
                      onChange={(e) => handleInputChange(field.store_as, e.target.value)}
                      className="literary-input w-full p-4 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 font-sans"
                      placeholder="Enter Score (0-50)"
                    />
                  ) : (
                    <input
                      type="text"
                      value={formData[field.store_as] || ''}
                      onChange={(e) => handleInputChange(field.store_as, e.target.value)}
                      className="literary-input w-full p-4 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 font-sans"
                      placeholder="Enter your response..."
                    />
                  )}
                </div>
              ))}
            </motion.div>
          ))
        ) : (
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="bg-white rounded-2xl shadow-soft p-8 paper-texture"
          >
            <h2 className="font-serif text-2xl text-primary mb-8 flex items-center gap-3">
              <SafeIcon icon={FiHeart} className="text-accent" />
              Reflection Questions
            </h2>
            <div className="space-y-8">
              {lesson.fields && lesson.fields.map((field, index) => (
                <div key={index}>
                  <label className="block font-sans font-semibold text-gray-700 mb-3 text-lg">
                    {field.label}
                  </label>
                  <textarea
                    value={formData[field.store_as] || ''}
                    onChange={(e) => handleInputChange(field.store_as, e.target.value)}
                    className="literary-input w-full p-4 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 font-sans"
                    rows={4}
                    placeholder="Share your thoughts..."
                  />
                </div>
              ))}
            </div>
            {lesson.note && (
              <div className="mt-8 p-6 bg-blue-50 border-l-4 border-primary rounded-r-xl">
                <p className="font-sans text-gray-700 italic leading-relaxed">
                  {lesson.note}
                </p>
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Save Button */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.3 }} 
        className="flex justify-center"
      >
        <button 
          onClick={handleManualSave}
          className="bg-primary text-white px-10 py-4 rounded-xl font-sans font-semibold hover:bg-blue-700 transition-all duration-300 flex items-center gap-3 btn-hover shadow-medium"
        >
          <SafeIcon icon={FiSave} className="text-xl" />
          Save Progress
        </button>
      </motion.div>

      {/* Faithful Focus */}
      {lesson.sections?.find(s => s.header.includes('Faithful Focus')) && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.4 }} 
          className="text-center py-12 px-8 bg-gradient-to-r from-blue-50 via-orange-50 to-blue-50 rounded-2xl shadow-soft paper-texture"
        >
          <SafeIcon icon={FiHeart} className="text-4xl text-accent mx-auto mb-6" />
          <p className="font-serif text-xl text-gray-700 italic leading-relaxed max-w-2xl mx-auto">
            {lesson.sections.find(s => s.header.includes('Faithful Focus')).content}
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default LessonPage;